import moment from 'moment'
import React, { FC, useMemo, useRef } from 'react'

import { Chart, Settings, LIGHT_THEME, PartialTheme, BarSeries, Axis, Position, ScaleType } from '@elastic/charts'
import { EuiPanel, EuiTitle, EuiText, EuiFlexGroup, EuiFlexItem, EuiBadge, EuiSpacer, useGeneratedHtmlId, EuiButtonIcon } from '@elastic/eui'

import { CampaignType } from 'api/interfaces'
import { MasterCampaignPlan, MasterCampaignPlanCustomSection } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignStub, MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { getEndDate, getLatestEndDate } from 'utils/CampaignMathUtils'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

export interface DeliveryMetricsProps {
  masterCampaignStub: MasterCampaignStub
  masterCampaignPlan: MasterCampaignPlan
  deliveryMetricsRef?: React.RefObject<HTMLDivElement>
  section?: MasterCampaignPlanCustomSection
  editMode?: boolean
  onEdit?: () => void
}

interface MetricData {
  channel: CampaignType
  channelName: string
  icon: string
}

interface ExtendedMetricData extends MetricData {
  campaignId?: string
  offAdCritterName?: string
  color?: string
}

interface MonthlyData {
  month: string
  cpm: number | null
  min: number | null
  projected: number | null
  graphProjected: number | null
  max: number | null
  graphMax: number | null
  marginOfError: number | null
}

const calculateMetrics = (builders: MasterCampaignPlanCampaignBuilder[]) => {
  const buildersWithCpm = builders.filter(builder => builder.cpm && builder.cpm > 0)

  if (buildersWithCpm.length === 0) {
    return null
  }

  return buildersWithCpm.reduce((sum, builder) => sum + builder.cpm!, 0) / buildersWithCpm.length
}

const calculateMonthlyBudget = (campaign: MasterCampaignPlanCampaignBuilder, daysInMonth: number): number => {
  if (!campaign.intervalUnit || !campaign.intervalFrequency) {
    return campaign.intervalBudget
  }

  switch (campaign.intervalUnit) {
    case 'Day':
      return (campaign.intervalBudget / campaign.intervalFrequency) * daysInMonth
    case 'Week':
      return (campaign.intervalBudget / campaign.intervalFrequency / 7) * daysInMonth
    case 'Month':
      return campaign.intervalBudget / campaign.intervalFrequency
    default:
      return 0
  }
}

const calculateMonthlyImpressions = (campaigns: MasterCampaignPlanCampaignBuilder[], monthMoment: moment.Moment) => {
  return campaigns.reduce((totalImpressions, campaign) => {
    if (campaign.cpm && campaign.cpm > 0 && campaign.intervalBudget) {
      const campaignStart = moment(campaign.startDate)
      const campaignEnd = getEndDate(campaign)

      if (!campaignEnd) {
        return totalImpressions
      }

      const daysInMonth = monthMoment.daysInMonth()
      const monthStart = monthMoment.clone().startOf('month')
      const monthEnd = monthMoment.clone().endOf('month')

      const effectiveStart = moment.max(campaignStart.clone().startOf('day'), monthStart)
      const effectiveEnd = moment.min(campaignEnd.clone().endOf('day'), monthEnd)

      if (effectiveEnd.isBefore(effectiveStart)) {
        return totalImpressions
      }

      const activeDaysInMonth = effectiveEnd.diff(effectiveStart, 'days') + 1
      const monthlyBudget = calculateMonthlyBudget(campaign, daysInMonth)
      const dailyBudget = monthlyBudget / daysInMonth
      const dailyImpressions = (dailyBudget / campaign.cpm) * 1000
      const campaignImpressions = dailyImpressions * activeDaysInMonth

      return totalImpressions + campaignImpressions
    }
    return totalImpressions
  }, 0)
}

const generateMonthlyData = (campaignBuilders: MasterCampaignPlanCampaignBuilder[], marginOfError: number): MonthlyData[] => {
  const buildersWithCpm = campaignBuilders.filter(builder => builder.cpm && builder.cpm > 0)
  if (buildersWithCpm.length === 0) {
    return []
  }

  const earliestStart = campaignBuilders.reduce((earliest, builder) => {
    const startDate = moment(builder.startDate)
    return !earliest || startDate.isBefore(earliest) ? startDate : earliest
  }, null as moment.Moment | null)

  const latestEnd = getLatestEndDate(campaignBuilders)

  if (!earliestStart || !latestEnd) {
    return []
  }

  const totalMonths = Math.max(12, latestEnd.diff(earliestStart, 'months') + 1)
  const months: MonthlyData[] = []

  for (let i = 0; i < totalMonths; i++) {
    const monthMoment = moment(earliestStart).add(i, 'months')
    const monthFull = monthMoment.format('MMMM YYYY')

    const activeCampaigns = campaignBuilders.filter(builder => {
      const builderStart = moment(builder.startDate)
      const builderEnd = getEndDate(builder)
      return monthMoment.isBetween(builderStart, builderEnd, 'month', '[]')
    })

    if (activeCampaigns.length === 0) {
      months.push({
        month: monthFull,
        cpm: null,
        min: null,
        projected: null,
        graphProjected: null,
        max: null,
        graphMax: null,
        marginOfError: null
      })
      continue
    }

    const activeCampaignsWithCpm = activeCampaigns.filter(builder => builder.cpm && builder.cpm > 0)

    if (activeCampaignsWithCpm.length === 0) {
      months.push({
        month: monthFull,
        cpm: null,
        min: null,
        projected: null,
        graphProjected: null,
        max: null,
        graphMax: null,
        marginOfError: null
      })
      continue
    }

    const monthlyAverageCpm = calculateMetrics(activeCampaignsWithCpm)

    if (!monthlyAverageCpm) {
      months.push({
        month: monthFull,
        cpm: null,
        min: null,
        projected: null,
        graphProjected: null,
        max: null,
        graphMax: null,
        marginOfError: null
      })
      continue
    }

    const projected = calculateMonthlyImpressions(activeCampaignsWithCpm, monthMoment)

    const obj = {
      month: monthFull,
      cpm: monthlyAverageCpm,
      projected: Math.round(projected),
      min: Math.round(projected - projected * marginOfError),
      max: Math.round(projected + projected * marginOfError),
      marginOfError: marginOfError
    } as MonthlyData

    obj.graphProjected = Math.round(obj.projected! - obj.min!)
    obj.graphMax = Math.round(obj.max! - obj.projected!)

    months.push(obj)
  }

  return months
}

const DeliveryMetrics: FC<DeliveryMetricsProps> = ({ masterCampaignStub, masterCampaignPlan, deliveryMetricsRef, section, editMode, onEdit }) => {
  const impressionChartId = useGeneratedHtmlId()
  const resizeRef = useRef<HTMLDivElement>(null)
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth)

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const impressionDistributionInternetData = useMemo(() => {
    if (!masterCampaignStub?.id || !masterCampaignPlan.internetMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.Internet),
      masterCampaignPlan.internetMarginOfError
    )
  }, [masterCampaignStub?.id, masterCampaignStub.campaignBuilders, masterCampaignPlan.internetMarginOfError])

  const impressionDistributionTvData = useMemo(() => {
    if (!masterCampaignStub?.id || !masterCampaignPlan.tvMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.TV),
      masterCampaignPlan.tvMarginOfError
    )
  }, [masterCampaignStub?.id, masterCampaignStub.campaignBuilders, masterCampaignPlan.tvMarginOfError])

  const impressionDistributionBillboardData = useMemo(() => {
    if (!masterCampaignStub?.id || !masterCampaignPlan.billboardMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard),
      masterCampaignPlan.billboardMarginOfError
    )
  }, [masterCampaignStub?.id, masterCampaignStub.campaignBuilders, masterCampaignPlan.billboardMarginOfError])

  const impressionDistributionOffAdCritterData = useMemo(() => {
    if (!masterCampaignStub?.id) {
      return new Map<string, MonthlyData[]>()
    }

    const offAdCritterCampaigns = masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpm && builder.cpm > 0 && builder.minCpm && builder.maxCpm && builder.marginOfError)

    const dataMap = new Map<string, MonthlyData[]>()

    offAdCritterCampaigns.forEach(campaign => {
      const monthlyData = generateMonthlyData([campaign], campaign.marginOfError!)
      if (monthlyData.length > 0) {
        dataMap.set(campaign.id, monthlyData)
      }
    })

    return dataMap
  }, [masterCampaignStub?.id, masterCampaignStub.campaignBuilders])

  const chartTheme: PartialTheme = {
    axes: {
      tickLabel: {
        fontSize: 12,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
      }
    },
    colors: {
      vizColors: ['transparent', '#d47a3a', '#d47a3a']
    }
  }

  const metricsData: ExtendedMetricData[] = useMemo(() => {
    const socialMediaPlatforms = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok']
    const metrics: ExtendedMetricData[] = []

    if (impressionDistributionTvData.length > 0) {
      metrics.push({
        channel: CampaignType.TV,
        channelName: 'Connected TV',
        icon: 'videoPlayer'
      })
    }

    if (impressionDistributionInternetData.length > 0) {
      metrics.push({
        channel: CampaignType.Internet,
        channelName: 'Internet',
        icon: 'globe'
      })
    }

    const offAdCritterCampaigns = masterCampaignStub?.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpm && builder.cpm > 0 && builder.minCpm && builder.maxCpm && builder.marginOfError && impressionDistributionOffAdCritterData.has(builder.id)) || []

    socialMediaPlatforms.forEach(platformName => {
      const campaign = offAdCritterCampaigns.find(c => c.offAdCritterName === platformName)
      if (campaign) {
        metrics.push({
          channel: CampaignType.Internet,
          channelName: platformName,
          icon: 'globe',
          campaignId: campaign.id,
          offAdCritterName: campaign.offAdCritterName,
          color: campaign.offAdCritterColor
        })
      }
    })

    if (impressionDistributionBillboardData.length > 0) {
      metrics.push({
        channel: CampaignType.Billboard,
        channelName: 'Digital Billboard',
        icon: 'mapMarker'
      })
    }

    const customOptions = masterCampaignPlan.offAdCritterCustomOptions || []
    const sortedCustomOptions = [...customOptions].sort((a, b) => a.order - b.order)

    sortedCustomOptions.forEach(customOption => {
      const campaign = offAdCritterCampaigns.find(c => c.offAdCritterName === customOption.name)
      if (campaign && !socialMediaPlatforms.includes(customOption.name)) {
        metrics.push({
          channel: CampaignType.Internet,
          channelName: customOption.name,
          icon: 'globe',
          campaignId: campaign.id,
          offAdCritterName: campaign.offAdCritterName,
          color: campaign.offAdCritterColor
        })
      }
    })

    return metrics
  }, [impressionDistributionTvData.length, impressionDistributionInternetData.length, impressionDistributionBillboardData.length, impressionDistributionOffAdCritterData, masterCampaignStub?.campaignBuilders, masterCampaignPlan.offAdCritterCustomOptions])

  let tilesPerRow = 3
  if (windowWidth < 1660) {
    tilesPerRow = 2
  }

  const getRowForItem = (totalItems: number, itemIndex: number): number => {
    if (totalItems <= 3) {
      return 0
    }

    if (totalItems === 4) {
      return itemIndex < 2 ? 0 : 1
    }

    const fullRows = Math.floor(totalItems / 3)
    const remainder = totalItems % 3

    if (remainder === 0) {
      return Math.floor(itemIndex / 3)
    }

    if (remainder === 1) {
      const itemsInFirstNMinusOneRows = (fullRows - 1) * 3
      if (itemIndex < itemsInFirstNMinusOneRows) {
        return Math.floor(itemIndex / 3)
      }
      return fullRows - 1 + Math.floor((itemIndex - itemsInFirstNMinusOneRows) / 2)
    }

    if (remainder === 2) {
      const itemsInFullRows = fullRows * 3
      if (itemIndex < itemsInFullRows) {
        return Math.floor(itemIndex / 3)
      }
      return fullRows
    }

    return Math.floor(itemIndex / 3)
  }

  const getItemsInRow = (totalItems: number, rowIndex: number): number => {
    if (totalItems <= 3) {
      return totalItems
    }

    if (totalItems === 4) {
      return 2
    }

    const fullRows = Math.floor(totalItems / 3)
    const remainder = totalItems % 3

    if (remainder === 0) {
      return 3
    }

    if (remainder === 1) {
      if (rowIndex >= fullRows - 1) {
        return 2
      }
      return 3
    }

    if (remainder === 2) {
      if (rowIndex === fullRows) {
        return 2
      }
      return 3
    }

    return 3
  }

  const organizeMetricsIntoRows = (metrics: ExtendedMetricData[], tilesPerRow: number): ExtendedMetricData[][] => {
    const totalItems = metrics.length

    if (totalItems === 0) {
      return []
    }

    if (totalItems <= tilesPerRow) {
      return [metrics]
    }

    if (totalItems === 4 && tilesPerRow === 2) {
      return [metrics.slice(0, 2), metrics.slice(2, 4)]
    }

    const fullRows = Math.floor(totalItems / tilesPerRow)
    const remainder = totalItems % tilesPerRow
    const rows: ExtendedMetricData[][] = []

    if (remainder === 0) {
      for (let i = 0; i < fullRows; i++) {
        rows.push(metrics.slice(i * tilesPerRow, (i + 1) * tilesPerRow))
      }
      return rows
    }

    if (remainder === 1 && tilesPerRow === 3) {
      for (let i = 0; i < fullRows - 1; i++) {
        rows.push(metrics.slice(i * tilesPerRow, (i + 1) * tilesPerRow))
      }
      const remaining = metrics.slice((fullRows - 1) * tilesPerRow)
      rows.push(remaining.slice(0, 2))
      rows.push(remaining.slice(2, 4))
      return rows
    }

    for (let i = 0; i < fullRows; i++) {
      rows.push(metrics.slice(i * tilesPerRow, (i + 1) * tilesPerRow))
    }
    if (remainder > 0) {
      rows.push(metrics.slice(fullRows * tilesPerRow))
    }

    return rows
  }

  const metricsRows = useMemo(() => organizeMetricsIntoRows(metricsData, tilesPerRow), [metricsData, tilesPerRow])

  const yAccessorFn = (d: MonthlyData) => d.graphProjected
  yAccessorFn.fieldName = 'Projected'

  if (section?.hideAll === true) {
    return null
  }

  const title = section?.title ?? 'CPM and Impressions'
  const content = section?.content ?? 'CPM and impression projections are based on real-time publisher data and historic campaign performance. The noted margin of error is due to inherent cost variation in digital ad auctions.'
  const showTitle = section?.showTitle ?? true

  return (
    <EuiPanel paddingSize='l'>
      <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
        <EuiFlexItem grow={false}>
          {showTitle && (
            <EuiTitle size='m'>
              <h2>{title}</h2>
            </EuiTitle>
          )}
        </EuiFlexItem>
        {editMode && onEdit && (
          <EuiFlexItem grow={false}>
            <EuiButtonIcon iconType='pencil' onClick={onEdit} aria-label='Edit' />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
        {content}
      </EuiText>
      <EuiSpacer size='l' />

      <div ref={resizeRef}>
        <div ref={deliveryMetricsRef}>
          {metricsRows.map((row, rowIndex) => (
            <>
              <EuiFlexGroup gutterSize='l' wrap={false} key={`row-${rowIndex}`}>
                {row.map((metric, metricIndex) => {
                  const index = metricsRows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + metricIndex
                  let impressionDistributionData: MonthlyData[] = []
                  let targetCpm = 0
                  let marginOfError = 0

                  if (metric.campaignId) {
                    impressionDistributionData = impressionDistributionOffAdCritterData.get(metric.campaignId) || []
                  } else {
                    switch (metric.channel) {
                      case CampaignType.Internet:
                        impressionDistributionData = impressionDistributionInternetData
                        break
                      case CampaignType.TV:
                        impressionDistributionData = impressionDistributionTvData
                        break
                      case CampaignType.Billboard:
                        impressionDistributionData = impressionDistributionBillboardData
                        break
                      default:
                        impressionDistributionData = []
                    }
                  }

                  if (impressionDistributionData.length > 0) {
                    const validDataPoints = impressionDistributionData.filter(d => d.cpm && d.marginOfError)
                    if (validDataPoints.length > 0) {
                      targetCpm = validDataPoints.reduce((sum, d) => sum + (d.cpm || 0), 0) / validDataPoints.length
                      marginOfError = validDataPoints[0].marginOfError || 0
                    }
                  }

                  const totalMinImpression = impressionDistributionData.reduce((min, d) => min + (d.min || 0), 0)
                  const totalProjectedImpression = impressionDistributionData.reduce((projected, d) => projected + (d.projected || 0), 0)
                  const totalMaxImpression = impressionDistributionData.reduce((max, d) => max + (d.max || 0), 0)

                  const minImpression = impressionDistributionData.reduce((min, d) => Math.min(min, d.min || min), Number.POSITIVE_INFINITY)
                  const maxImpression = impressionDistributionData.reduce((max, d) => Math.max(max, d.max || 0), 0)

                  return (
                    <EuiFlexItem key={index} grow={3}>
                      <EuiPanel
                        paddingSize='l'
                        hasShadow={true}
                        style={{
                          height: '100%',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                        }}>
                        <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
                          <EuiFlexItem grow={false}>
                            <EuiFlexGroup alignItems='center' gutterSize='s'>
                              <EuiFlexItem grow={false}>
                                <EuiTitle size='xs'>
                                  <h3>{metric.channelName}</h3>
                                </EuiTitle>
                              </EuiFlexItem>
                            </EuiFlexGroup>
                          </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size='m' />

                        <EuiFlexGroup alignItems='baseline'>
                          <EuiFlexItem grow={false}>
                            <EuiText size='s'>Projected Avg. CPM</EuiText>
                          </EuiFlexItem>
                          <EuiFlexItem grow={false}>
                            <EuiBadge color='#F0B051'>
                              <EuiText size='m' style={{ fontWeight: 'bold' }}>
                                {formatCurrency(targetCpm)}
                              </EuiText>
                            </EuiBadge>
                          </EuiFlexItem>
                          <EuiFlexItem grow={false}>
                            <EuiText size='xs' color='subdued'>
                              ±{formatPercentage(marginOfError)}
                            </EuiText>
                          </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size='m' />

                        <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                          {metric.channelName === 'Internet' ? 'Total Projected Impressions' : 'Total Projected Views'}
                        </EuiText>
                        <EuiFlexGroup gutterSize='s'>
                          <EuiFlexItem>
                            <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                              <EuiText size='xs' color='subdued'>
                                Minimum
                              </EuiText>
                              <EuiText size='m' color='subdued'>
                                {formatNumber(totalMinImpression, 0, 0)}
                              </EuiText>
                            </EuiPanel>
                          </EuiFlexItem>
                          <EuiFlexItem>
                            <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)', borderStyle: 'solid', borderColor: '#F0B051' }}>
                              <EuiText size='xs' style={{ fontWeight: 'bold' }}>
                                Projected
                              </EuiText>
                              <EuiText size='m' style={{ fontWeight: 'bold' }}>
                                {formatNumber(totalProjectedImpression, 0, 0)}
                              </EuiText>
                            </EuiPanel>
                          </EuiFlexItem>
                          <EuiFlexItem>
                            <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                              <EuiText size='xs' color='subdued'>
                                Maximum
                              </EuiText>
                              <EuiText size='m' color='subdued'>
                                {formatNumber(totalMaxImpression, 0, 0)}
                              </EuiText>
                            </EuiPanel>
                          </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size='m' />

                        <div>
                          <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            {metric.channelName === 'Internet' ? 'Impression by Month' : 'Views by Month'}
                          </EuiText>
                          <EuiPanel paddingSize='s' style={{ backgroundColor: 'var(--euiColorEmptyShade)' }}>
                            <div style={{ height: '300px', position: 'relative' }}>
                              <Chart id={impressionChartId + index} size={{ height: 300 }}>
                                <Settings
                                  theme={chartTheme}
                                  baseTheme={LIGHT_THEME}
                                  showLegend={false}
                                  tooltip={{
                                    customTooltip: ({ values }) => {
                                      const dataPoint = values.find(v => (v.seriesIdentifier as any).yAccessor === 'Projected')
                                      if (!dataPoint) {
                                        return null
                                      }

                                      const monthlyData = dataPoint.datum as MonthlyData

                                      if (!monthlyData.projected || !monthlyData.min || !monthlyData.max) {
                                        return null
                                      }

                                      return (
                                        <EuiPanel paddingSize='s' hasShadow={true} style={{ minWidth: '140px' }}>
                                          <EuiTitle size='s'>
                                            <h3 style={{ marginBottom: '4px' }}>{monthlyData.month}</h3>
                                          </EuiTitle>
                                          <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                            Projected: {formatNumber(monthlyData.projected, 0, 0)}
                                          </EuiText>
                                          <EuiText size='xs' color='subdued'>
                                            Min: {formatNumber(monthlyData.min, 0, 0)}
                                          </EuiText>
                                          <EuiText size='xs' color='subdued'>
                                            Max: {formatNumber(monthlyData.max, 0, 0)}
                                          </EuiText>
                                        </EuiPanel>
                                      )
                                    }
                                  }}
                                />
                                <BarSeries
                                  id='Projected'
                                  data={impressionDistributionData}
                                  xAccessor='month'
                                  yAccessors={['min', yAccessorFn, 'graphMax']}
                                  stackAccessors={['min', yAccessorFn, 'graphMax']}
                                  xScaleType={ScaleType.Ordinal}
                                  yScaleType={ScaleType.Linear}
                                  tickFormat={value => formatNumber(value, 0, 0)}
                                  filterSeriesInTooltip={(d: any) => d.seriesKeys.includes('Projected')}
                                  barSeriesStyle={{
                                    rectBorder: {
                                      visible: true,
                                      stroke: 'transparent',
                                      strokeWidth: 0.5
                                    }
                                  }}
                                />
                                <Axis id='impressions' position={Position.Left} tickFormat={value => formatNumber(value, 0, 0)} domain={{ min: 0, max: maxImpression + maxImpression * 0.2 }} />
                                <Axis
                                  id='months'
                                  position={Position.Bottom}
                                  style={{
                                    tickLabel: {
                                      rotation: 0
                                    }
                                  }}
                                  labelFormat={d => d.charAt(0)}
                                />
                              </Chart>
                            </div>
                          </EuiPanel>
                        </div>
                      </EuiPanel>
                    </EuiFlexItem>
                  )
                })}
              </EuiFlexGroup>
              {rowIndex < metricsRows.length - 1 && <EuiSpacer size='l' />}
            </>
          ))}
        </div>
      </div>

      <EuiSpacer size='l' />
    </EuiPanel>
  )
}

export default DeliveryMetrics
